import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCardComponent from "../../components/ModalCardComponent.tsx";
import {
  convertProjectContextToProject,
  EmptyProject,
} from "../../models/Project.ts";
import { useFetch } from "../../services/hooks/useFetch";
import { apiPrefix } from "../../utils/main";
import { deviceApiPrefix } from "../../utils/main.ts";
import { projectSetupSteps } from "./steps/stepsConfig.ts";

export default function CreateProject({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [changes, setChanges] = useState({});
  const [currStep, setCurrStep] = useState(0);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disableNext, setDisableNext] = useState(true);
  const [buttonText, setButtonText] = useState({
    left: "Cancel",
    right: "Next",
  });
  const [companySettingMinDate, setCompanySettingMinDate] = useState(
    new Date(2018),
  );

  const projectData = useMemo(() => {
    return { ...EmptyProject, ...changes };
  }, [changes]);
  const {
    data: createdProjectData,
    isLoading: projectSaving,
    create: createProject,
    error: createProjectError,
  } = useFetch<Project, Project>();
  const { isLoading, fetch: fetchCompanySettings } = useFetch<
    Setting[],
    Setting
  >();

  function onUpdateData(newChanges: Partial<ProjectContextType>) {
    setChanges((prev) => {
      return { ...prev, ...newChanges };
    });
  }

  async function handleNext() {
    const isDataCompleted = projectSetupSteps[currStep].validation;
    if (!isDataCompleted(projectData, companySettingMinDate)) {
      setDisplayErrors(true);
      setDisableNext(true);
      return;
    }
    setDisplayErrors(false);
    setDisableNext(false);
    if (currStep === projectSetupSteps.length - 1) {
      setLoading(true);
      void handleCreateProject(projectData);
    } else {
      const nextStep = currStep + 1;
      setCurrStep(nextStep);
    }
  }

  function handleBack() {
    if (currStep === 0) {
      onCancel();
      return;
    }
    setDisplayErrors(false);
    setDisableNext(false);
    setCurrStep((prev) => --prev);
  }

  async function handleCreateProject(projectData: ProjectContextType) {
    const newProject = convertProjectContextToProject(projectData);
    void createProject({ path: `${apiPrefix}/projects`, body: newProject });
  }

  useEffect(() => {
    if (!projectSaving && createdProjectData && !!createdProjectData.id) {
      navigate(`/details?projectId=${createdProjectData.id}`);
    }
  }, [projectSaving, createdProjectData, navigate]);

  useEffect(() => {
    const isDataCompleted = projectSetupSteps[currStep].validation;
    setDisableNext(!isDataCompleted(projectData, companySettingMinDate));
  }, [companySettingMinDate, currStep, projectData]);

  useEffect(() => {
    void fetchCompanySettings(`${deviceApiPrefix}/companies/settings`, {
      postResponseAction: (data) => {
        const s = data!.filter((s: Setting) => s.name === "START_DATE_MIN")[0];
        if (s) {
          setCompanySettingMinDate(new Date(parseInt(s.value), 0));
        }
      },
    });
  }, [fetchCompanySettings]);

  useEffect(() => {
    if (currStep === projectSetupSteps.length - 1) {
      setButtonText({ left: "Back", right: `Create Project` });
      return;
    }
    if (currStep === 0) {
      setButtonText({ left: "Cancel", right: "Next" });
      return;
    }
    setButtonText({ left: "Back", right: "Next" });
  }, [currStep]);

  useEffect(() => {
    if (createProjectError) {
      setLoading(false);
      setDisplayErrors(true);
    }
  }, [createProjectError]);

  function renderProjectSetupSteps() {
    return projectSetupSteps.map((item, index) => {
      const { component: Component, id } = item;
      const props = {
        displayContent: index === currStep,
        displayErrors,
        createProjectError,
        onUpdateData,
        projectData,
        companySettingMinDate,
      };
      // To see the component rendered in a step, use stepsConfig.ts as reference
      return <Component key={id} {...props} />;
    });
  }

  const ModalContent = (
    <div className="project-drawer-content">{renderProjectSetupSteps()}</div>
  );

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onCancel}
      slotProps={{
        paper: {
          sx: { width: "min(100%, 700px)" },
        },
      }}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        <ModalCardComponent
          header={
            <div className="header">
              <Typography className="drawer-title" variant="h5">
                Start a Project
              </Typography>
              <IconButton
                onClick={onCancel}
                sx={{ "&:hover": { bgcolor: "action.hover" } }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          }
          content={ModalContent}
          footer={
            <Box
              className={`standard-drawer-footer`}
              sx={{ bgcolor: "common.white" }}
            >
              <Button onClick={handleBack} variant="outlined">
                {buttonText.left}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={loading && <CircularProgress size="1.5rem" />}
                disabled={disableNext || loading}
              >
                {buttonText.right}
              </Button>
            </Box>
          }
          classes={`create-project-drawer`}
        />
      )}
    </Drawer>
  );
}
